import { useCallback } from 'react';
import { css } from '@emotion/react';
import { useQueryClient } from 'react-query';
import { Commands1 } from '@frontend/api-commands';
import { getInvoiceQueryKey } from '@frontend/api-invoices';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import {
  BannerNotification,
  ContentLoader,
  CopyToClipboardButton,
  Heading,
  Text,
  useAlert,
} from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { NavigationHeader } from '../../atoms/navigation-header';
import { StyledStep } from '../../atoms/styled-step';
import { CareCreditPrequalifyBanner } from './care-credit-prequalify-banner';
import { PaymentMethodsButtons } from './payment-methods-buttons';
import { PaymentMethodsStepBodyProps } from './payment-methods.types';
import { PaymentSummary } from './PaymentSummary';
import { PaymentSurchargeLine } from './PaymentSurchargeLine';

const styles = {
  stepOverrides: css`
    .step__body {
      padding: 0;
      margin: ${theme.spacing(1)} 0 0;
    }
  `,
  summary: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)} ${theme.spacing(3)};
  `,
  amount: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(4)};
    flex-wrap: wrap;
  `,
  paymentMethods: css`
    align-content: stretch;

    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    flex: 1;
    gap: ${theme.spacing(2)};

    & > button {
      height: ${theme.spacing(8)};
    }

    @media (max-width: ${breakpoints.small.max}px) {
      align-content: flex-start;
    }
  `,
  paymentsHeadingLegacy: css`
    padding: ${theme.spacing(2)} ${theme.spacing(3)};
  `,
  footer: css`
    display: flex;
    gap: ${theme.spacing(2)};
    align-items: center;
    justify-content: space-between;
  `,
  alreadyPaidBanner: {
    container: css`
      background-color: ${theme.colors.white};
    `,
    banner: css`
      margin: ${theme.spacing(2, 2, 0, 2)};
    `,
  },
  wrapper: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    row-gap: ${theme.spacing(2)};
    padding: ${theme.spacing(0, 3)};
  `,
  divider: css`
    border-top: solid 1px ${theme.colors.neutral20};
  `,
};

export const PaymentMethodsBody = ({ onClickBack, onClickPrimary, back }: PaymentMethodsStepBodyProps) => {
  const { t } = useTranslation('payments');
  const alerts = useAlert();

  const queryClient = useQueryClient();

  const {
    invoice,
    invoiceQuery: { refetch, isLoading, isFetching, isRefetching },
    invoiceIsPaid,
    activeGateway,
    surchargeEnabled,
    surchargePercentValue,
    careCreditEnrollmentStatus,
    careCreditData,
    isCareCreditLoading,
    multiStep: { isResponsiveMode },
  } = useCollectPaymentMultiContext();

  const { laterTrackingId, copyTrackingId, surchargeLine } = useScopedTrackingIds({
    laterTrackingId: 'collect-later-btn',
    copyTrackingId: 'copy-btn',
    surchargeLine: 'surcharge-line',
  });

  const { mutateAsync: toggleRequestSurchargeMutation } =
    Commands1.Mutations.useSetPaymentRequestSurchargingEnabledMutation({
      optimisticUpdate: false,
      options: {
        onMutate: ({ enableSurcharging }) => {
          if (invoice) {
            const queryKey = getInvoiceQueryKey(invoice.id);

            queryClient.cancelQueries(queryKey);

            queryClient.setQueryData(queryKey, {
              ...invoice,
              surchargingEnabled: enableSurcharging ?? false,
              configuredSurchargeBasisPoints: surchargePercentValue,
            });
          }

          const shouldRefetch = isRefetching || isFetching || isLoading;

          return { shouldRefetch, invoiceId: invoice?.id };
        },
        onSuccess: (_data, _variables, context) => {
          const { shouldRefetch } = context ?? {};
          if (shouldRefetch) {
            refetch();
          }
        },
        onError: (_error, { enableSurcharging }) => {
          if (invoice) {
            const queryKey = getInvoiceQueryKey(invoice.id);

            queryClient.setQueryData(queryKey, { ...invoice, surchargingEnabled: !enableSurcharging });
          }
        },
      },
    });

  const onChangeSurcharge = useCallback(
    async (active: boolean) => {
      try {
        await toggleRequestSurchargeMutation({
          paymentRequestId: invoice?.id,
          enableSurcharging: active,
        });
      } catch (err) {
        alerts.error(t('Error updating invoice surcharge enabled state. Please try again.'));
        console.error('Error updating invoice surcharge enabled state', err);
      }
    },
    [toggleRequestSurchargeMutation, invoice?.id]
  );

  return (
    <StyledStep
      id={PAYMENT_MULTI_STEPS.paymentMethods}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.paymentMethods]}
      isValid={true}
      css={styles.stepOverrides}
      components={
        isResponsiveMode
          ? {
              Header: () => <NavigationHeader title={t('Take Payment')} />,
            }
          : undefined
      }
      footer={{
        secondary: () => (
          <CopyToClipboardButton
            variant='tertiary'
            textToCopy={invoice?.links.payment ?? ''}
            tooltipText={t('Copy link')}
            tooltipSuccessText={t('Link copied!')}
            hasSuccessTooltip
            hasIcon={true}
            icon={() => <Icon name='link-small' color={invoiceIsPaid ? 'disabled' : 'primary'} size={24} />}
            trackingId={copyTrackingId}
            disabled={invoiceIsPaid}
          >
            <Text weight='bold' color={invoiceIsPaid ? 'disabled' : 'primary'}>
              {t('Copy Link')}
            </Text>
          </CopyToClipboardButton>
        ),
        primary: {
          children: invoiceIsPaid ? t('Close') : t('Collect Later'),
          trackingId: laterTrackingId,
          variant: 'secondary',
        },
        back,
        onClickPrimary: onClickPrimary,
        onClickBack: onClickBack,
      }}
    >
      {(isLoading || !invoice) && <ContentLoader show={true} />}

      {invoice && (
        <div css={styles.wrapper}>
          {invoiceIsPaid && (
            <div css={styles.alreadyPaidBanner.container}>
              <BannerNotification
                css={styles.alreadyPaidBanner.banner}
                fullWidth
                status='success'
                message={t('This payment has already been made successfully')}
              />
            </div>
          )}
          <PaymentSummary invoice={invoice}>
            {activeGateway === 'stripe' && (
              <PaymentSurchargeLine
                trackingId={surchargeLine}
                invoice={invoice}
                show={surchargeEnabled}
                onChange={onChangeSurcharge}
              />
            )}
          </PaymentSummary>

          <div css={styles.divider}></div>

          {careCreditEnrollmentStatus?.status === 'declined' && (
            <BannerNotification status='error' message={careCreditEnrollmentStatus.message} />
          )}

          <Heading level={3}>{t('Choose Collection Method')}</Heading>

          <section css={styles.paymentMethods}>
            <PaymentMethodsButtons />
          </section>

          {!isCareCreditLoading && careCreditData?.status === 'not-enrolled' && <CareCreditPrequalifyBanner />}
        </div>
      )}
    </StyledStep>
  );
};
